import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import { fetchDAPrice, fetchIntradayPrice } from '@/services/api/priceApi'
import type { components } from '../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

export function useSimulatorSessions(poolID: string, start: Date, end: Date, enabled = true) {
  return useQuery({
    queryKey: ['simulator-sessions', poolID, start.toISOString(), end.toISOString()],
    queryFn: async () => {
      const res = await apiService.monitoring.getChargingSessions(poolID, {
        start: start.toISOString(),
        end: end.toISOString(),
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch sessions')
      return (res.data ?? []) as ChargingSession[]
    },
    enabled: !!poolID && enabled,
    staleTime: Infinity, // charging sessions are immutable historical records
    placeholderData: (prev) => prev,
  })
}

const START_OF_2026 = new Date('2026-01-01T00:00:00Z').getTime()

export function useDAPrice(start: Date, end: Date, enabled = true) {
  return useQuery({
    queryKey: ['da-price', start.toISOString(), end.toISOString()],
    queryFn: () => fetchDAPrice(start, end),
    enabled: enabled && end.getTime() > START_OF_2026,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: (prev) => prev,
  })
}

export function useIntradayPrice(start: Date, end: Date, enabled = true) {
  return useQuery({
    queryKey: ['intraday-price', start.toISOString(), end.toISOString()],
    queryFn: () => fetchIntradayPrice(start, end),
    enabled: enabled && end.getTime() > START_OF_2026,
    staleTime: 60 * 60 * 1000,
    retry: 2,
    placeholderData: (prev) => prev,
  })
}
